<template>
  <div class="reservation-params">
    <field
      v-for="(param, index) of reservationParameters"
      v-if="displayField(param.id)"
      :key="'additional-params-' + index + '-' + param.id">
      <label slot="label">
        <template v-if="param.type !== 'infotext'">{{ translate('parameters', param.id, 'name', param.name) }}</template>
        <template v-if="param.type === 'infotext'">{{ $t('reservationFormParams.information') }}</template>
      </label>

      <small
        v-if="param.type === 'infotext'"
        slot="field"
        class="field-desc"
        v-pure-html="translate('parameters', param.id, 'name', param.name)">
      </small>

      <input
        v-if="param.type === 'text'"
        v-model="paramsValues[param.id]"
        :class="{ 'is-invalid': errors.indexOf('field-' + param.id) > -1 }"
        :key="'additional-param-field-' + param.id"
        slot="field"
        @input="clearError('field-' + param.id)" />

      <textarea
        v-if="param.type === 'textarea'"
        v-model="paramsValues[param.id]"
        :class="{ 'is-invalid': errors.indexOf('field-' + param.id) > -1 }"
        :key="'additional-param-field-' + param.id"
        @input="clearError('field-' + param.id)"
        slot="field"></textarea>

      <select
        v-if="param.type === 'select'"
        v-model="paramsValues[param.id]"
        :class="{ 'is-invalid': errors.indexOf('field-' + param.id) > -1 }"
        slot="field"
        :key="'additional-param-field-' + param.type + '-' + param.id"
        @change="clearError('field-' + param.id)">
        <option :key="'additional-param-field-' + param.id + '-option-00'" :value="undefined">{{ $t('reservationFormParams.selectValue') }}</option>
        <option
          v-for="(opt, oindex) of param.values"
          :key="'additional-param-field-' + param.id + '-option-' + oindex"
          :value="opt.id">
          {{ translate('parameterValues', opt.id, 'value', opt.value) }}
          <template v-if="showAdditionalPrices && paymentsEnabled && opt.price !== '0.00'">
            ({{ getFormattedPrice(opt.price, $parent.$parent.priceCurrency) }})
          </template>

          <template v-if="opt.extra_minutes">
             (<template v-if="opt.extra_minutes > 0">+</template>{{ opt.extra_minutes }} min)
          </template>
        </option>
      </select>

      <multi-switcher
        v-if="param.type === 'checkbox'"
        slot="field"
        v-model="paramsValues[param.id]"
        :class="{ 'is-invalid': errors.indexOf('field-' + param.id) > -1 }"
        :showAdditionalPrices="showAdditionalPrices"
        :key="'additional-param-field-' + param.id"
        :options="param.values"
        @click="clearError('field-' + param.id)"
        :currency="$parent.$parent.priceCurrency" />

      <small
        v-if="errors.indexOf('field-' + param.id) > -1"
        slot="note"
        class="note">
        {{ $t('reservationFormParams.thisParameterIsRequired') }}
      </small>

      <small
        v-if="paymentsEnabled && (param.type === 'select' || param.type === 'checkbox') && selectedParamHasAdditionalPrice(param.id, param.type)"
        slot="note"
        class="note">
        {{ $t('reservationFormParams.givenPriceForAdditionalFeatures') }}
      </small>

      <small
        v-if="param.required && errors.indexOf('field-' + param.id) === -1"
        slot="note"
        class="note">
        {{ $t('reservationFormParams.parameterIsRequired') }}
      </small>
    </field>
  </div>
</template>

<script>
import Vue from 'vue';
import Configuration from './../mixins/Configuration';
import CurrencyUtils from './../helpers/CurrencyUtils';
import FormHelpers from './../mixins/FormHelpers';
import MultiSwitcher from './../common/MultiSwitcher';

export default {
  name: 'reservation-form-params',
  mixins: [
    Configuration,
    FormHelpers
  ],
  props: {
    value: {
      required: true
    },
    selectedService: {
      required: true
    },
    reservationParameters: {
      required: true
    },
    showSpecialParams: {
      default: false
    },
    showAdditionalPrices: {
      default: true
    },
    useDaysMode: {
      default: false
    }
  },
  components: {
    'multi-switcher': MultiSwitcher
  },
  data () {
    return {
      errors: [],
      paramsValues: {}
    };
  },
  mounted () {
    setTimeout(() => {
      this.paramsValues = this.value;
    }, 0);
  },
  watch: {
    'value': function (newValue) {
      this.paramsValues = newValue;
    },
    'paramsValues': {
      handler: function (newValue) {
        let existingValues = Object.keys(newValue);

        for (let i = 0; i < existingValues.length; i++) {
          if (newValue[existingValues[i]] !== '') {
            this.clearError('field-' + existingValues[i]);
          }
        }

        this.$emit('input', newValue);
      },
      deep: true
    }
  },
  methods: {
    displayField (paramID) {
      if (this.showSpecialParams === false) {
        let showField = (this.paramUsedBySelectedService(paramID) || this.isGlobalParam(paramID)) && !this.isSpecialParam(paramID);

        if (showField) {
          this.setFirstValueIfNecessary(paramID);
        }

        return showField;
      }

      let showField = (this.paramUsedBySelectedService(paramID) || this.isGlobalParam(paramID)) && this.isSpecialParam(paramID);

      if (this.useDaysMode && !this.isRefreshingCalendarParam(paramID)) {
        showField = false;
      }

      if (showField) {
        this.setFirstValueIfNecessary(paramID);
      }

      return showField;
    },
    setFirstValueIfNecessary (paramID) {
      // do not set first value if previous selected
      if (this.paramsValues && this.paramsValues[paramID]) {
        return;
      }

      let paramConfig = this.reservationParameters.filter(param => param.id === paramID);

      if (paramConfig && paramConfig[0] && paramConfig[0].type === 'select') {
        let firstSelected = paramConfig[0].first_selected;

        if (firstSelected && paramConfig[0].values && paramConfig[0].values[0]) {
          Vue.set(this.paramsValues, paramID, paramConfig[0].values[0].id);
        }
      }
    },
    paramUsedBySelectedService (paramID) {
      return this.reservationParameters
        .filter(param => param.id === paramID)[0].services
        .filter(service => service.id === this.selectedService).length > 0;
    },
    isGlobalParam (paramID) {
      let services = this.reservationParameters.filter(param => param.id === paramID)[0].services;
      return !services || (services && services.length === 0);
    },
    isSpecialParam (paramID) {
      let param = this.reservationParameters.filter(param => param.id === paramID)[0];
      return param.update_hours === 1 || param.refresh_calendar === 1;
    },
    isRefreshingCalendarParam (paramID) {
      let param = this.reservationParameters.filter(param => param.id === paramID)[0];
      return param.refresh_calendar === 1;
    },
    selectedParamHasAdditionalPrice (paramID, paramType) {
      let param = this.reservationParameters.filter(param => param.id === paramID)[0];

      if (!param) {
        return false;
      }

      if (paramType === 'select') {
        let value = param.values.filter(option => option.id === this.paramsValues[paramID])[0];

        if (!value) {
          return false;
        }

        return value.price !== '0.00';
      }

      if (paramType === 'checkbox' && this.paramsValues[paramID]) {
        let values = param.values.filter(option => this.paramsValues[paramID].indexOf(option.id) > -1);

        for (let i = 0; i < values.length; i++) {
          if (values[i].price !== '0.00') {
            return true;
          }
        }
      }

      return false;
    },
    generateAdditionalParamsObject () {
      let paramsObject = [];
      let paramIDs = Object.keys(this.paramsValues);

      for (let i = 0; i < paramIDs.length; i++) {
        let param = {
          id: paramIDs[i],
          value: '',
          value_id: null
        };
        let paramType = this.typeOfParamByID(paramIDs[i]);

        switch (paramType) {
          case 'text':
          case 'textarea':
            param.value = this.paramsValues[paramIDs[i]];
            break;
          case 'select':
            param.value = this.getSelectParamValue(paramIDs[i], this.paramsValues[paramIDs[i]]);
            param.value_id = this.paramsValues[paramIDs[i]];

            if (param.value === '') {
              continue;
            }

            break;
          case 'checkbox':
            param.value = this.getCheckboxParamValues(paramIDs[i], this.paramsValues[paramIDs[i]]);
            param.value_id = this.paramsValues[paramIDs[i]];
            break;
          case 'infotext':
            continue;
        }

        paramsObject.push(param);
      }

      return paramsObject;
    },
    typeOfParamByID (paramID) {
      return this.reservationParameters
        .filter(param => param.id === parseInt(paramID, 10))[0].type;
    },
    getSelectParamValue (paramID, valueID) {
      if (!valueID) {
        return '';
      }

      return this.reservationParameters
        .filter(param => param.id === parseInt(paramID, 10))[0].values
        .filter(value => value.id === parseInt(valueID, 10))[0].value;
    },
    getCheckboxParamValues (paramID, valueIDs) {
      let values = [];
      let valuesData = this.reservationParameters.filter(param => param.id === parseInt(paramID, 10))[0].values;

      for (let i = 0; i < valuesData.length; i++) {
        if (valueIDs.indexOf(valuesData[i].id) > -1) {
          values.push(valuesData[i].value);
        }
      }

      return values;
    },
    validate () {
      let result = true;

      if (!this.reservationParameters) {
        return result;
      }

      for (let i = 0; i < this.reservationParameters.length; i++) {
        let paramID = this.reservationParameters[i].id;

        if (!this.reservationParameters[i].required) {
          continue;
        }

        if (this.reservationParameters[i].type === 'checkbox') {
          if (this.displayField(paramID) && (!this.paramsValues[paramID] || !this.paramsValues[paramID].length)) {
            this.errors.push('field-' + paramID);
            result = false;
          }
        } else if (this.displayField(paramID)) {
          if (this.paramsValues[paramID] && this.paramsValues[paramID] !== '') {
            continue;
          } else {
            this.errors.push('field-' + paramID);
            result = false;
          }
        }
      }

      return result;
    },
    getFormattedPrice (price, currency) {
      if (price > 0) {
        return CurrencyUtils.getQuote(price, currency);
      }

      return CurrencyUtils.getQuote(price, currency);
    },
    translate (type, key, fieldName = false, defaultPhrase = '') {
      if (this.$parent.$parent.translate) {
        return this.$parent.$parent.translate(type, key, fieldName, defaultPhrase);
      }

      return defaultPhrase;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.reservation-params {
  width: 100%;

  select {
    &.is-invalid {
      border: 1px solid $color-danger;

      & + .note {
        color: $color-danger;
      }
    }
  }

  .is-invalid {
    border: 1px solid $color-danger;

    & + .note {
      color: $color-danger;
    }
  }

  .multi-switcher {
    border: none!important;
  }

  .field-desc {
    max-width: 60%;

    /deep/ bookero-iframe {
      background: $color-bg-light;
      border: 1px solid $color-border;
      display: block;
      min-height: 50px;
      padding: 16px;
      width: 100%;
      word-break: break-all;

      &:after {
        content: 'Film do wyświetlenia: ' attr(src);
      }
    }
  }
}

@media (max-width: 800px) {
  .reservation-params {
    .field {
      input,
      select,
      textarea {
        width: 66%;
      }
    }
  }
}

@media (max-width: 580px) {
  .reservation-params {
    .field {
      label,
      input,
      select,
      textarea {
        text-align: left;
        width: 100%;
      }

      small.note {
        padding: .5rem 0 0 0;
        width: 100%;
      }

      label {
        margin-bottom: .5rem;
      }

      .medium-select {
        width: 30%;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/ReservationFormParams.vue